(function () {
  if (window.productCardMediaSwatchesInitialized) return;

  window.productCardMediaSwatchesInitialized = true;

  const SWATCH_SELECTOR =
    '.product-card__swatch-list .media-swatch[data-primary-media-url]';
  const imageCache = new Map();

  function buildImageUrl(sourceUrl, width, format) {
    const imageUrl = new URL(sourceUrl, window.location.origin);

    imageUrl.searchParams.set('width', width);

    if (format) {
      imageUrl.searchParams.set('format', format);
    } else {
      imageUrl.searchParams.delete('format');
    }

    return imageUrl.toString();
  }

  function getOptimizedUrl(sourceUrl) {
    if (!sourceUrl) return null;

    return buildImageUrl(sourceUrl, 800, 'webp');
  }

  function preloadImage(url) {
    if (!url) return Promise.resolve(null);

    if (imageCache.has(url)) {
      return imageCache.get(url);
    }

    const promise = new Promise(function (resolve, reject) {
      const image = new Image();

      image.decoding = 'async';
      image.src = url;

      function resolveImage() {
        if (typeof image.decode === 'function') {
          image.decode().catch(function () {}).finally(function () {
            resolve(image);
          });
        } else {
          resolve(image);
        }
      }

      if (image.complete && image.naturalWidth > 0) {
        resolveImage();
      } else {
        image.addEventListener('load', resolveImage, { once: true });
        image.addEventListener('error', reject, { once: true });
      }
    });

    imageCache.set(url, promise);

    return promise;
  }

  function getSwatchUrls(swatch) {
    return {
      primary: getOptimizedUrl(swatch.dataset.primaryMediaUrl),
      hover: getOptimizedUrl(swatch.dataset.hoverMediaUrl)
    };
  }

  function preloadSwatch(swatch) {
    const urls = getSwatchUrls(swatch);

    return Promise.allSettled([
      preloadImage(urls.primary),
      preloadImage(urls.hover)
    ]);
  }

  function removePictureSources(picture) {
    if (!picture) return;

    picture.querySelectorAll('source').forEach(function (source) {
      source.removeAttribute('srcset');
    });
  }

  function setSelectedSwatch(productCard, selectedSwatch) {
    productCard.querySelectorAll(SWATCH_SELECTOR).forEach(function (swatch) {
      const input = swatch.previousElementSibling;
      const selected = swatch === selectedSwatch;

      swatch.classList.toggle('is-selected', selected);

      if (input && input.matches('input')) {
        input.checked = selected;
      }
    });
  }

  function showCachedThumbnail(primaryImage, swatch) {
    const thumbnail = swatch.querySelector('img');

    if (!thumbnail) return;

    const thumbnailUrl =
      thumbnail.currentSrc || thumbnail.getAttribute('src');

    if (!thumbnailUrl) return;

    primaryImage.removeAttribute('srcset');
    primaryImage.loading = 'eager';
    primaryImage.src = thumbnailUrl;
  }

  function observeSwatches(root) {
    const swatches = root.querySelectorAll
      ? root.querySelectorAll(SWATCH_SELECTOR)
      : [];

    swatches.forEach(function (swatch) {
      if (swatch.dataset.preloadObserved === 'true') return;

      swatch.dataset.preloadObserved = 'true';
      swatchPreloadObserver.observe(swatch);
    });
  }

  const swatchPreloadObserver = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        preloadSwatch(entry.target);
        swatchPreloadObserver.unobserve(entry.target);
      });
    },
    {
      rootMargin: '300px 0px',
      threshold: 0.01
    }
  );

  document.addEventListener(
    'pointerover',
    function (event) {
      const swatch = event.target.closest(SWATCH_SELECTOR);

      if (!swatch) return;

      /*
       * Impact has its own product-card swatch hover handler. The custom
       * media swatch markup does not use that variant-preview contract,
       * so stop the old handler before it reaches the product card.
       */
      event.stopPropagation();
      preloadSwatch(swatch);
    },
    true
  );

  document.addEventListener(
    'touchstart',
    function (event) {
      const swatch = event.target.closest(SWATCH_SELECTOR);

      if (swatch) preloadSwatch(swatch);
    },
    { passive: true }
  );

  document.addEventListener('click', function (event) {
    const swatch = event.target.closest(SWATCH_SELECTOR);

    if (!swatch) return;

    event.preventDefault();
    event.stopPropagation();

    const productCard = swatch.closest('product-card');
    const primaryImage = productCard?.querySelector(
      '.product-card__image--primary'
    );
    const secondaryImage = productCard?.querySelector(
      '.product-card__image--secondary'
    );

    if (!productCard || !primaryImage) return;

    const primaryPicture = primaryImage.closest('picture');
    const secondaryPicture = secondaryImage?.closest('picture');
    const urls = getSwatchUrls(swatch);
    const requestId =
      Date.now().toString() + Math.random().toString(36);

    productCard.dataset.swatchRequestId = requestId;
    productCard.classList.add('product-card--media-switching');

    setSelectedSwatch(productCard, swatch);
    removePictureSources(primaryPicture);
    showCachedThumbnail(primaryImage, swatch);

    const swatchName = swatch
      .querySelector('.sr-only')
      ?.textContent
      ?.trim();

    if (swatchName) {
      primaryImage.alt = swatchName;
    }

    if (urls.hover && secondaryImage && secondaryPicture) {
      removePictureSources(secondaryPicture);
      secondaryImage.removeAttribute('srcset');
      productCard.classList.add('product-card--show-secondary-media');
    } else {
      productCard.classList.remove('product-card--show-secondary-media');
    }

    const primaryReady = preloadImage(urls.primary).then(function () {
      if (productCard.dataset.swatchRequestId !== requestId) return;

      if (urls.primary) {
        primaryImage.src = urls.primary;
      }
    });

    const hoverReady = preloadImage(urls.hover).then(function () {
      if (productCard.dataset.swatchRequestId !== requestId) return;

      if (urls.hover && secondaryImage && secondaryPicture) {
        secondaryImage.src = urls.hover;
        secondaryPicture.style.removeProperty('display');
      }
    });

    Promise.allSettled([primaryReady, hoverReady]).then(function () {
      if (productCard.dataset.swatchRequestId !== requestId) return;

      requestAnimationFrame(function () {
        if (productCard.dataset.swatchRequestId !== requestId) return;

        productCard.classList.remove('product-card--media-switching');
      });
    });
  });

  document.addEventListener('DOMContentLoaded', function () {
    observeSwatches(document);
  });

  document.addEventListener('shopify:section:load', function (event) {
    observeSwatches(event.target);
  });

  if (document.readyState !== 'loading') {
    observeSwatches(document);
  }
})();
